import { ReactNode } from "react";

import { css } from "@compiled/react";

import { Image, ImageProps } from "../../../components/wpl/image/src";

const COLORS = {
    n500: "#42526e",
    n700: "#253858",
    n900: "#091e42",
    n0: "#ffffff"
};

const heroStyle = css({
    "display": "grid",
    "gridTemplateColumns": "1fr",
    "alignItems": "center",
    "gap": "2rem",
    "maxWidth": "1200px",
    "margin": "auto",
    "padding": "3.75rem 1rem 5rem",

    "@media only screen and (min-width: 768px)": {
        gridTemplateColumns: "1fr 1fr"
    }
});
const contentStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: "2.5rem"
});
const headingBasicStyles = css({
    letterSpacing: 0,
    marginBlockStart: 0,
    marginBlockEnd: 0,
    fontWeight: 500
});
const h1Style = css({
    "fontSize": "2rem",
    "lineHeight": 1.25,
    "marginBlockEnd": "14px",

    "@media only screen and (min-width: 768px)": {
        fontSize: "2.75rem",
        lineHeight: 1.18181818,
        marginBlockEnd: "16px"
    },
    "@media only screen and (min-width: 1440px)": {
        fontSize: "3rem",
        lineHeight: "3.5rem",
        marginBlockEnd: "24px"
    }
});
const h3Style = css({
    "fontSize": "1.25rem",
    "letterSpacing": "0.3px",
    "lineHeight": 1.2,

    "@media only screen and (min-width: 768px)": {
        fontSize: "1.5rem",
        lineHeight: 1.33333333
    }
});
const h4Style = css({
    "fontSize": "1rem",
    "letterSpacing": ".3px",
    "lineHeight": "1.25",

    "@media only screen and (min-width: 768px)": {
        fontSize: "1.25rem",
        lineHeight: "1.2"
    }
});
const h5Style = css({
    "fontSize": "0.875rem",
    "fontWeight": 900,
    "letterSpacing": "1px",
    "lineHeight": 1.25,
    "textTransform": "uppercase",
    "marginBlockEnd": "14px",

    "@media only screen and (min-width: 1024px)": {
        marginBlockEnd: "24px"
    }
});
const listStyle = css({
    "listStyleType": "none",
    "paddingInlineStart": "20px",
    "marginBlock": "0",

    "li": {
        marginInlineStart: "-4px",
        minHeight: "15px",
        paddingInlineStart: "16px",
        position: "relative",
        marginBlockStart: "24px",
        color: "inherit",
        fontSize: "1rem",
        lineHeight: "1.5"
    },
    "li::before": {
        content: "",
        backgroundImage: `url("data:image/svg+xml,%3Csvg version='1.2' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 30' width='16' fill='rgb(9, 30, 66)' height='15'%3E%3Cpath d='m1.5 16.4c1.8-1.7 4.2-0.3 4.2-0.3l5.6 5.9 15.4-18.7c0 0 1.7-1.2 3.5 0.1 1.9 1.4 0.8 3.6 0.8 3.6l-18 22h-2l-9.1-8.7c0 0-2.1-2.2-0.4-3.9z'%3E%3C/path%3E%3C/svg%3E")`,
        width: "1rem",
        height: "1rem",
        position: "absolute",
        insetInlineStart: "-1rem",
        insetBlockStart: ".25rem"
    }
});
const imgStyle = css({
    verticalAlign: "bottom"
});
const subTertiaryTextStyle = css({
    margin: "0",
    fontSize: "1.25em",
    letterSpacing: ".3px",
    lineHeight: "1.4"
});
const themes = css({
    ".dark-on-light": {
        "color": COLORS.n900,
        "h1,h3,h4": {
            color: COLORS.n700
        },
        "h5": {
            color: COLORS.n500
        }
    },
    ".light-on-dark": {
        color: COLORS.n0,
        ul: {
            "li::before": {
                backgroundImage: `url("data:image/svg+xml,%3Csvg version='1.2' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 30' width='16' fill='white' height='15'%3E%3Cpath d='m1.5 16.4c1.8-1.7 4.2-0.3 4.2-0.3l5.6 5.9 15.4-18.7c0 0 1.7-1.2 3.5 0.1 1.9 1.4 0.8 3.6 0.8 3.6l-18 22h-2l-9.1-8.7c0 0-2.1-2.2-0.4-3.9z'%3E%3C/path%3E%3C/svg%3E")`
            }
        }
    }
});

export interface PaidHeroProps {
    headerText: string;
    secondaryText?: string;
    featuresLabel?: string;
    featuresList?: string[];
    image?: ImageProps;
    tertiaryText?: string;
    subTertiaryText?: ReactNode;
    theme?: "light-on-dark" | "dark-on-light";
}

export const PaidHero = ({
    headerText,
    secondaryText,
    featuresLabel,
    featuresList,
    image,
    tertiaryText,
    subTertiaryText,
    theme = "dark-on-light"
}: PaidHeroProps) => {
    return (
        <div css={[heroStyle, themes]}>
            <div className={theme}>
                <div css={contentStyle}>
                    <div>
                        <h1 css={[headingBasicStyles, h1Style]}>
                            {headerText}
                        </h1>
                        {secondaryText && (
                            <h3 css={[headingBasicStyles, h3Style]}>
                                {secondaryText}
                            </h3>
                        )}
                    </div>
                    {(featuresLabel || featuresList?.length) && (
                        <div>
                            {featuresLabel && (
                                <h5 css={[headingBasicStyles, h5Style]}>
                                    {featuresLabel}
                                </h5>
                            )}
                            {featuresList?.length && (
                                <ul css={listStyle}>
                                    {featuresList.map((feature) => (
                                        <li key={feature}>{feature}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                    {image && <Image {...image} css={imgStyle} />}
                    {tertiaryText && (
                        <h4 css={[headingBasicStyles, h4Style]}>
                            {tertiaryText}
                        </h4>
                    )}
                    {subTertiaryText && (
                        <p
                            className="payHero-subTertiary"
                            css={[headingBasicStyles, subTertiaryTextStyle]}
                        >
                            {subTertiaryText}
                        </p>
                    )}
                </div>
                <div className="placeholder" />
            </div>
        </div>
    );
};
